/* ======================================================================
 * File: src/pages/mixer/ColorMixerPortal.jsx
 * ----------------------------------------------------------------------
 * React page for the Colour-Match Mixer portal.
 * – Server-driven navigation      – LocalStorage cache for fast refresh
 * – Verbose console logging for the three access gates
 * ==================================================================== */

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HostCard from '../../components/mixer/Color Mixer UI Components/HostCard';
import MixerPortalHeaderBar      from '../../components/mixer/Color Mixer UI Components/MixerPortalNavBar';
import MixerPortalHeader         from '../../components/mixer/Color Mixer UI Components/MixerPortalHeader';
import MixerInstructionsBanner   from '../../components/mixer/Color Mixer UI Components/MixerInstructionsBanner';
import EventDetailsCard          from '../../components/mixer/Color Mixer UI Components/EventDetailsCard';
import ColorRevealAndCodeInput   from '../../components/mixer/ColorRevealAndCodeInput';
import MixerMatchingSuccess      from '../../components/mixer/MixerMatchingSuccess';
import MixerIcebreakerScreen     from '../../components/mixer/MixerIcebreakerScreen';
import MinimalFooter             from '../../components/MinimalFooter';

import useMixerSocket            from '../../utils/useMixerSocket';
import mixerApi                  from '../../utils/mixerApi';
import { useToaster }            from '../../context/ToasterContext';
import EndOfEventFeedbackModal from '../../components/EndOfEventFeedbackModal';
/* -------------------------------------------------- *
 * Helpers
 * -------------------------------------------------- */
const haversineMi = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8;                 // Earth radius in miles
  const toRad = d => (d * Math.PI) / 180;
  const dLat  = toRad(lat2 - lat1);
  const dLon  = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const CACHE_VERSION = 1;
const LS_KEY = evtId => `mixerState_${evtId}_v${CACHE_VERSION}`;

const isValidSnap = s => s && typeof s.phase === 'string';
const isExpired   = s => s?.endsAt && new Date(s.endsAt).getTime() <= Date.now();
const safeParse   = str => { try { return JSON.parse(str); } catch { return null; } };

const readCache = evtId => {
  const snap = safeParse(localStorage.getItem(LS_KEY(evtId)));
  return isValidSnap(snap) && !isExpired(snap) ? snap : null;
};
const persistSnap = (evtId, snap) => {
  if (isValidSnap(snap) && snap.phase !== 'portal') {
    localStorage.setItem(LS_KEY(evtId), JSON.stringify(snap));
  }
};

/* make relative URLs absolute against API base */
const toAbsolute = (url) => {
  const s = (url ?? '').toString().trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const path = s.startsWith('/') ? s : `/${s}`;
  return base ? `${base}${path}` : path;
};

/* Default lobby snapshot */
const portalSnap = { phase: 'portal' };

/* Format "First Last" → "First L." (used for pills and headings) */
const formatName = (first = '', last = '') => {
  const f = String(first).trim();
  const l = String(last).trim();
  if (!f) return '';
  return l ? `${f} ${l[0].toUpperCase()}.` : f;
};

/* Hetero Mixer detector */
async function isHeteroMixer(eventId) {
  try {
    const { data: evt } = await axios.get(
      `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    return (
      (evt?.mode === 'mixer' || evt?._id) &&
      String(evt?.purposeTag || '').toLowerCase() === 'dating' &&
      String(evt?.orientationType || '').toLowerCase() === 'heterosexual'
    );
  } catch {
    return false;
  }
}

/* ======================================================================
 * COMPONENT
 * ==================================================================== */
export default function MixerSection({ event }) {
  /* ─── router / context ───────────────────────────────────────── */
  const { eventId }   = useParams();                 // router param is bare Mongo ID
  const userId        = localStorage.getItem('userId');
  const navigate      = useNavigate();
  const { addToast }  = useToaster();

  /* fetch your user once (for avatar, server-driven like ProfilePage) */
  const [myProfilePicUrl, setMyProfilePicUrl] = useState('');
  const [myFirstName, setMyFirstName] = useState('');
  const [myLastName,  setMyLastName]  = useState('');

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        setMyProfilePicUrl(data?.profilePicUrl || '');
        setMyFirstName((data?.firstName || '').trim());
        setMyLastName((data?.lastName  || '').trim());
      } catch (err) {
        console.warn('[Mixer] fetch current user failed:', err?.response?.status || err?.message);
        setMyProfilePicUrl('');
        setMyFirstName('');
        setMyLastName('');
      }
    })();
  }, [userId]);

  const myDisplayName = useMemo(
    () => formatName(myFirstName, myLastName) || (localStorage.getItem('firstName') || '').trim() || 'You',
    [myFirstName, myLastName]
  );

  /* ─── local cache (anti-flicker) ─────────────────────────────── */
  const cachedSnap = useMemo(() => readCache(eventId), [eventId]);

  /* ─── geo-fence check (single shot) ──────────────────────────── */
  const [inFence, setInFence] = useState(null);      // null = pending
  useEffect(() => {
    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const myLat = coords.latitude;
        const myLng = coords.longitude;

        /* 2️⃣ client-side calc (distance) ------------------------------- */
        try {
          const { data: evt } = await axios.get(
            `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
          );
          const fenceMi = (evt.radius ?? 0) / 5280;
          const distMi  = haversineMi(myLat, myLng, evt.latitude ?? 0, evt.longitude ?? 0);
          const accuracyFt = ((coords.accuracy ?? 30) * 3.28084);        // meters → feet
          const slackFt    = Math.max(75, Math.min(150, accuracyFt));    // clamp 75–150 ft
          const ok         = !fenceMi || (distMi * 5280) <= ((evt.radius ?? 0) - slackFt);
          
          console.log('[GeoFence] client calc →', {
            myLat, myLng,
            eventLat : evt.latitude,
            eventLng : evt.longitude,
            radiusFt : evt.radius,
            radiusMi : fenceMi.toFixed(3),
            distMi   : distMi.toFixed(3),
            ok,
          });

          if (!cancelled) setInFence(ok);
        } catch (err) {
          console.error('[GeoFence] client calc failed:', err);
          if (!cancelled) setInFence(false);
        }
      },
      err => {
        console.error('[GeoFence] geolocation error:', err);
        setInFence(false);
      }
    );

    return () => { cancelled = true; };
  }, [eventId]);

  /* ─── access-guard (registration + quiz + geo) ───────────────── */
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (!firstRunRef.current || inFence === null) return;   // wait for geo
    firstRunRef.current = false;
  
    const previewPath = `/events/mixer-${eventId}`;         // public page
  
    (async () => {
      try {
        // ✅ Server-side combined access check (registration + quiz + geo)
        await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}/access`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        console.log('[GateKeeper] /access check → ok');
      } catch (err) {
        const status  = err?.response?.status;
        const reason  = err?.response?.data?.reason || err?.response?.data?.message || '';
        // If denial looks like "quiz required", but this is a hetero mixer → bypass
        if ((status === 401 || status === 403) && /quiz|answer|complete/i.test(String(reason))) {
          if (await isHeteroMixer(eventId)) {
            console.log('[GateKeeper] quiz denied but hetero mixer → bypassing quiz gate');
            // stay in portal
            return;
          }
        }
        console.warn(
          '[GateKeeper] /access denied:',
          err?.response?.data || err.message
        );
        navigate(previewPath, { replace: true });
        return;
      }
  
      // (Optional) You can keep these for extra logging only
      try {
        /* registration */
        const { data: evt } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        const attendees = [
          ...(evt.attendeeIds || []),
          ...(Array.isArray(evt.attendees) ? evt.attendees.map(a => a._id || a) : []),
        ].map(String);
        console.log('[GateKeeper] registration gate →', {
          regOk: attendees.includes(String(userId)),
          total: attendees.length
        });
  
        /* quiz */
      const { data: quiz } = await mixerApi.fetchQuiz(eventId, userId);
 if (quiz?.disabled) {
   console.log('[GateKeeper] quiz gate → disabled (hetero mixer)');
 } else {
   console.log('[GateKeeper] quiz gate →', { quizOk: !!quiz?.alreadySubmitted });
 }
        /* geo */
        console.log('[GateKeeper] geo gate →', { inFence });
      } catch (logErr) {
        console.error('[GateKeeper] extra logging failed:', logErr);
      }
    })().catch(err => {
      console.error('[GateKeeper] unexpected error:', err);
      navigate('/events', { replace: true });
    });
  }, [eventId, userId, inFence, navigate]);
  

  // ─── backend-driven geofence watchdog (poll /access every 15s) ───────────────
  useEffect(() => {
    if (!eventId || !userId) return;

    let cancelled = false;
    let tId;

    const scheduleNext = () => { tId = setTimeout(tick, 15_000); };

    const kick = (msg = 'You left the event area. Returning to preview.') => {
      try { addToast?.(msg, 'error', 4000); } catch {}
      try { setInFence(false); } catch {}                 // tears down socket via unmount
      navigate(`/events/mixer-${eventId}`, { replace: true });
    };

    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}/access`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );

        // tolerate both { ok: boolean } or { geoOk: boolean }
        const ok = (data?.ok ?? data?.geoOk ?? true);
        if (!ok && !cancelled) {
          const reason = data?.reason || data?.message || '';
          if (/quiz|answer|complete/i.test(String(reason)) && (await isHeteroMixer(eventId))) {
            console.log('[Watchdog] quiz denial ignored for hetero mixer');
            return scheduleNext();
          }
          return kick();
        }

      } catch (err) {
        // If backend signals denial via 401/403, treat as out-of-fence.
        const s = err?.response?.status;
        if (!cancelled && (s === 401 || s === 403)) {
          const reason = err?.response?.data?.reason || err?.response?.data?.message || '';
          if (/quiz|answer|complete/i.test(String(reason)) && (await isHeteroMixer(eventId))) {
            console.log('[Watchdog] quiz denial ignored for hetero mixer');
            return scheduleNext();
          }
          return kick();
        }
        // Other transient errors are ignored; we'll try again next tick.
      } finally {
        if (!cancelled) scheduleNext();
      }
    };

    tick();

    return () => {
      cancelled = true;
      clearTimeout(tId);
    };
  }, [eventId, userId, addToast, navigate]);

  /* ─── feedback modal flag (opened by socket hook) ─────────────── */
  const [showFeedback, setShowFeedback] = useState(false); // 🆕

  /* ─── real-time socket snapshot ───────────────────────────────── */
  const { state: socketSnap, nextStartsAt, mixerEnded } = useMixerSocket(
    eventId,
    userId,
    inFence,
    {
      onUnmatched: () => addToast('You didn’t match in time!', 'error', 4000),
      // Open feedback when server emits 'mixer:ended' (once per event)
      onMixerEnded: () => {
        const key = `feedback_given_event_${eventId}`;
        if (localStorage.getItem(key) !== '1') {
          setShowFeedback(true);
        }
      },
    },
  );

  /* cache snapshot for fast refresh */
  useEffect(() => { persistSnap(eventId, socketSnap); }, [socketSnap, eventId]);

  /* If you prefer not to use the callback, this also works: */
  useEffect(() => {
    if (!mixerEnded) return;
    const key = `feedback_given_event_${eventId}`;
    if (localStorage.getItem(key) !== '1') {
      setShowFeedback(true);
    }
  }, [mixerEnded, eventId]);

  /* ─── choose best snapshot (anti-flicker merge) ───────────────── */
  const snap = useMemo(() => {
    if (isValidSnap(socketSnap) && !isExpired(socketSnap) && socketSnap.phase !== 'portal')
      return socketSnap;

    if (isValidSnap(socketSnap) && socketSnap.phase === 'portal' && socketSnap.epoch === 0)
      return socketSnap;

    if (
      isValidSnap(socketSnap) && !isExpired(socketSnap) &&
      socketSnap.phase === 'portal' &&
      (!cachedSnap || cachedSnap.phase !== 'portal' || socketSnap.epoch >= cachedSnap.epoch)
    ) return socketSnap;

    if (cachedSnap && !isExpired(cachedSnap)) return cachedSnap;

    return isValidSnap(socketSnap) && !isExpired(socketSnap) ? socketSnap : portalSnap;
  }, [socketSnap, cachedSnap]);

  /* ─── event meta + live occupancy ─────────────────────────────── */
  const [eventDetails, setEventDetails] = useState(null);
  // Put this right after: const [eventDetails, setEventDetails] = useState(null);
  const promoter = useMemo(() => {
    const p = eventDetails?.promoterId;
    return (p && typeof p === 'object') ? p : null; // handle object vs string
  }, [eventDetails]);

  const hostName = useMemo(() => {
    return (
      eventDetails?.hostName ||
      promoter?.displayName ||
      promoter?.name ||
      '' // no hardcoded placeholder here
    );
  }, [eventDetails, promoter]);

  const hostAvatar = useMemo(() => {
    const raw =
      promoter?.avatarUrl ||
      promoter?.profilePicUrl ||
      promoter?.imageUrl ||
      eventDetails?.hostAvatar ||
      '';

    if (raw) {
      // make relative image paths absolute (same trick as your Event page)
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return `${base}${path}`;
    }
    return `https://i.pravatar.cc/80?u=${encodeURIComponent(hostName || eventDetails?._id || 'host')}`;
  }, [promoter, eventDetails, hostName]);

  const promoterEmail = useMemo(() => {
    return (
      promoter?.email ||
      eventDetails?.promoterEmail ||
      eventDetails?.hostEmail ||
      ''
    );
  }, [promoter, eventDetails]);

  const [nextRoundStartsAt, setNextRoundStartsAt] = useState(null);
  const [occupancy, setOccupancy] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        setEventDetails(data);
      } catch (err) {
        console.error('[Mixer] fetch meta →', err);
      }
    })();
  }, [eventId]);

  useEffect(() => {
    const fetchOcc = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        setOccupancy(data.occupancyCount || 0);
      } catch {/* ignore */}
    };
    fetchOcc();
    const id = setInterval(fetchOcc, 5_000);
    return () => clearInterval(id);
  }, [eventId]);

  useEffect(() => {
    if (!eventDetails?.roundStartTimes?.length) return;
    const now  = Date.now();
    const next = eventDetails.roundStartTimes
      .map(ts => new Date(ts))
      .find(d => d.getTime() > now);
    setNextRoundStartsAt(next || null);
  }, [eventDetails]);

  /* ─── BLUE-side code submission helper ───────────────────────── */
  const [shouldShake, setShouldShake] = useState(false);
  const handleCodeSubmit = async code => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/mixer/events/${eventId}/match`,
        { redUserId: userId, inputCode: code },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
    } catch (err) {
      console.error('[CodeSubmit] failed:', err);
      setShouldShake(true);
    }
  };

  /* ─── MAIN RENDER ---------------------------------------------------- */
  const endsAt = snap?.endsAt ? new Date(snap.endsAt) : null;
  let content;

  switch (snap.phase) {
    case 'matching':
      content = (
        <ColorRevealAndCodeInput
          colour          ={snap.colour}
          endsAt          ={endsAt}
          codeValue       ={snap.code}
          onCodeSubmit    ={handleCodeSubmit}
          shouldShake     ={shouldShake}
          onShakeComplete ={() => setShouldShake(false)}
        />
      );
      break;

    case 'success':
      content = (
        <MixerMatchingSuccess
          yourName    ={myDisplayName}
          partnerName ={snap.partner?.name   || 'Your Partner'}
          partnerPic  ={toAbsolute(snap.partner?.avatar) || '/default-avatar.png'}
          yourPic     ={toAbsolute(myProfilePicUrl) || '/default-avatar.png'}
          endsAt      ={endsAt}
        />
      );
      break;

    case 'icebreaker':
      content = (
        <MixerIcebreakerScreen
          prompt      ={snap.icebreakerPrompt}
          partnerName ={snap.partner?.name || 'Your Partner'}
          endsAt      ={endsAt}
        />
      );
      break;

    default: /* lobby */
      content = (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1 }}>
            <MixerPortalHeaderBar
              mixerEventId={eventId}
              isPortalPage={true}   
              occupancy   ={occupancy}
              onExit={() => navigate('/events')}
            />

            <MixerPortalHeader
              userId            ={userId}
              firstName         ={localStorage.getItem('firstName')}
              eventName         ={eventDetails?.name || 'Event'}
              roundStartTimes   ={eventDetails?.roundStartTimes || []}
              nextRoundStartsAt ={nextStartsAt || nextRoundStartsAt}
            />

            <MixerInstructionsBanner />

            {eventDetails && (
              <>
                <EventDetailsCard event={eventDetails} promoter={eventDetails.promoterId} />
                <section style={{ maxWidth: 680, width: '94%', margin: '12px auto 0', padding: '0 12px' }}>
                  <HostCard
                    hostName={hostName || 'Host'}
                    hostAvatar={hostAvatar}
                    promoterEmail={promoterEmail}
                    eventName={eventDetails?.name}
                    eventDate={eventDetails?.eventDate}
                    hostCreatedAt={eventDetails?.promoterId?.hostSince || eventDetails?.promoterId?.createdAt}
                    idVerified={true}
                  />
                </section>
              </>
            )}
          </div>
          <MinimalFooter />
        </div>
      );
  }

  return (
    <>
      {content}

      {showFeedback && eventId && (
        <EndOfEventFeedbackModal
          open
          eventId={eventId}
          userId={userId}
          onClose={() => setShowFeedback(false)}
          onLeaveEvent={() => {
            setShowFeedback(false);
            navigate('/events', { replace: true });
          }}
        />
      )}
    </>
  );
}
